import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Wallet,
  ArrowDownLeft,
  ArrowUpRight,
  Building2,
  Plus,
  Trash2,
  Star,
  Loader2,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertCircle,
  CreditCard,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';

const WalletPage = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [showWithdrawDialog, setShowWithdrawDialog] = useState(false);
  const [showAddBankDialog, setShowAddBankDialog] = useState(false);
  const [addingBank, setAddingBank] = useState(false);
  const [bankForm, setBankForm] = useState({
    account_holder_name: '',
    bank_name: '',
    account_number: '',
    routing_number: '',
    account_type: 'checking'
  });

  useEffect(() => {
    if (user) {
      fetchWallet();
    }
  }, [user]);

  const fetchWallet = async () => {
    try {
      const response = await apiClient.get('/wallet/');
      setWallet(response.data);
    } catch (error) {
      console.error('Failed to fetch wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 10) {
      alert('Minimum withdrawal is $10');
      return;
    }
    if (amount > wallet.balance) {
      alert('Insufficient balance');
      return;
    }

    setWithdrawing(true);
    try {
      await apiClient.post('/wallet/withdraw', { amount });
      setShowWithdrawDialog(false);
      setWithdrawAmount('');
      fetchWallet();
      alert('Withdrawal request submitted! Funds will arrive in 3-5 business days.');
    } catch (error) {
      alert(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleAddBank = async () => {
    if (!bankForm.account_holder_name || !bankForm.bank_name || 
        !bankForm.account_number || !bankForm.routing_number) {
      alert('Please fill all fields');
      return;
    }

    // Frontend validation for numeric-only fields
    const accountNumCleaned = bankForm.account_number.replace(/\s/g, '');
    const routingNumCleaned = bankForm.routing_number.replace(/\s/g, '');
    
    if (!/^\d+$/.test(accountNumCleaned)) {
      alert('Account number must contain only digits (0-9)');
      return;
    }
    if (accountNumCleaned.length < 4 || accountNumCleaned.length > 17) {
      alert('Account number must be between 4 and 17 digits');
      return;
    }
    if (!/^\d{9}$/.test(routingNumCleaned)) {
      alert('Routing number must be exactly 9 digits');
      return;
    }

    setAddingBank(true);
    try {
      await apiClient.post('/wallet/bank-accounts', {
        ...bankForm,
        account_number: accountNumCleaned,
        routing_number: routingNumCleaned
      });
      setShowAddBankDialog(false);
      setBankForm({
        account_holder_name: '',
        bank_name: '',
        account_number: '',
        routing_number: '',
        account_type: 'checking'
      });
      fetchWallet();
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Handle Pydantic validation errors
      if (Array.isArray(detail)) {
        const messages = detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        alert(messages);
      } else {
        alert(detail || 'Failed to add bank account');
      }
    } finally {
      setAddingBank(false);
    }
  };

  const handleRemoveBank = async (accountId) => {
    if (!confirm('Remove this bank account?')) return;
    
    try {
      await apiClient.delete(`/wallet/bank-accounts/${accountId}`);
      fetchWallet();
    } catch (error) {
      alert('Failed to remove bank account');
    }
  };

  const handleSetDefault = async (accountId) => {
    try {
      await apiClient.put(`/wallet/bank-accounts/${accountId}/default`);
      fetchWallet();
    } catch (error) {
      alert('Failed to set default');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-500/20 text-emerald-400">{t('completed')}</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-400">{t('pending')}</Badge>;
      case 'processing':
        return <Badge className="bg-blue-500/20 text-blue-400">Processing</Badge>;
      case 'failed':
        return <Badge className="bg-red-500/20 text-red-400">{t('failed')}</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-400">Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <Wallet className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">{t('signInRequired')}</h2>
              <p className="text-muted-foreground mb-4">{t('pleaseSignInToWallet')}</p>
              <Button onClick={() => navigate('/login')}>{t('signIn')}</Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{t('myWallet')}</h1>
                <p className="text-muted-foreground">{t('manageFundsPayouts')}</p>
              </div>
            </div>
            <Dialog open={showWithdrawDialog} onOpenChange={setShowWithdrawDialog}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-gradient-to-r from-emerald-500 to-teal-600"
                  disabled={!wallet?.bank_accounts?.length || wallet?.balance < 10}
                >
                  <ArrowUpRight className="w-4 h-4 mr-2" /> {t('withdrawFundsBtn')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('withdrawFundsTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('enterAmountToWithdraw')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>{t('availableBalance')}</Label>
                    <p className="text-2xl font-bold text-foreground">
                      {formatCurrency(wallet?.balance || 0)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="amount">{t('withdrawalAmount')}</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="amount"
                        type="number"
                        placeholder="0.00"
                        value={withdrawAmount}
                        onChange={(e) => setWithdrawAmount(e.target.value)}
                        className="pl-9"
                        min="10"
                        max={wallet?.balance || 0}
                      />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowWithdrawDialog(false)}>
                    {t('cancel')}
                  </Button>
                  <Button onClick={handleWithdraw} disabled={withdrawing}>
                    {withdrawing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {t('withdrawFundsBtn')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('availableBalance')}</p>
                        <p className="text-2xl font-bold text-foreground">
                          {formatCurrency(wallet?.balance || 0)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-emerald-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('pending')}</p>
                        <p className="text-2xl font-bold text-foreground">
                          {formatCurrency(wallet?.pending_balance || 0)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-yellow-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('totalReceived')}</p>
                        <p className="text-2xl font-bold text-foreground">
                          {formatCurrency(wallet?.total_received || 0)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                        <ArrowDownLeft className="w-6 h-6 text-emerald-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t('totalWithdrawn')}</p>
                        <p className="text-2xl font-bold text-foreground">
                          {formatCurrency(wallet?.total_withdrawn || 0)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center">
                        <ArrowUpRight className="w-6 h-6 text-teal-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Tabs defaultValue="transactions" className="space-y-6">
                <TabsList>
                  <TabsTrigger value="transactions">{t('transactions')}</TabsTrigger>
                  <TabsTrigger value="bank-accounts">{t('bankAccounts')}</TabsTrigger>
                </TabsList>

                {/* Transactions Tab */}
                <TabsContent value="transactions">
                  <Card>
                    <CardHeader>
                      <CardTitle>{t('transactionHistoryTitle')}</CardTitle>
                      <CardDescription>{t('allWalletTransactions')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {wallet?.recent_transactions?.length > 0 ? (
                        <div className="space-y-4">
                          {wallet.recent_transactions.map((tx) => (
                            <div key={tx.id} className="flex items-center justify-between p-4 rounded-lg bg-accent/50">
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  tx.type === 'donation_received' 
                                    ? 'bg-emerald-500/20' 
                                    : 'bg-teal-500/20'
                                }`}>
                                  {tx.type === 'donation_received' ? (
                                    <ArrowDownLeft className="w-5 h-5 text-emerald-400" />
                                  ) : (
                                    <ArrowUpRight className="w-5 h-5 text-teal-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">{tx.description}</p>
                                  {tx.campaign_title && (
                                    <p className="text-sm text-muted-foreground">{tx.campaign_title}</p>
                                  )}
                                  <p className="text-xs text-muted-foreground">{formatDate(tx.created_at)}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className={`font-semibold ${
                                  tx.type === 'donation_received' ? 'text-emerald-400' : 'text-foreground'
                                }`}>
                                  {tx.type === 'donation_received' ? '+' : '-'}{formatCurrency(tx.amount)}
                                </p>
                                {getStatusBadge(tx.status)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">{t('noTransactionsYet')}</p>
                          <p className="text-sm text-muted-foreground">
                            {t('transactionsWillAppear')}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Bank Accounts Tab */}
                <TabsContent value="bank-accounts">
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle>{t('bankAccounts')}</CardTitle>
                          <CardDescription>{t('manageBankAccounts')}</CardDescription>
                        </div>
                        <Dialog open={showAddBankDialog} onOpenChange={setShowAddBankDialog}>
                          <DialogTrigger asChild>
                            <Button variant="outline">
                              <Plus className="w-4 h-4 mr-2" /> {t('addAccount')}
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>{t('addBankAccountTitle')}</DialogTitle>
                              <DialogDescription>
                                {t('addBankAccountDesc')}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>{t('accountHolderName')}</Label>
                                <Input
                                  value={bankForm.account_holder_name}
                                  onChange={(e) => setBankForm({...bankForm, account_holder_name: e.target.value})}
                                  placeholder="John Doe"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>{t('bankName')}</Label>
                                <Input
                                  value={bankForm.bank_name}
                                  onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})}
                                  placeholder="Chase, Bank of America, etc."
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>{t('accountNumber')}</Label>
                                  <Input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={bankForm.account_number}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^0-9]/g, '');
                                      setBankForm({...bankForm, account_number: value});
                                    }}
                                    placeholder="Enter digits only"
                                    maxLength={17}
                                  />
                                  <p className="text-xs text-muted-foreground">4-17 digits, numbers only</p>
                                </div>
                                <div className="space-y-2">
                                  <Label>{t('routingNumber')}</Label>
                                  <Input
                                    type="tel"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                    value={bankForm.routing_number}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^0-9]/g, '');
                                      setBankForm({...bankForm, routing_number: value});
                                    }}
                                    placeholder="9 digits"
                                    maxLength={9}
                                  />
                                  <p className="text-xs text-muted-foreground">Exactly 9 digits</p>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <Label>{t('accountType')}</Label>
                                <Select
                                  value={bankForm.account_type}
                                  onValueChange={(v) => setBankForm({...bankForm, account_type: v})}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="checking">{t('checking')}</SelectItem>
                                    <SelectItem value="savings">{t('savings')}</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => setShowAddBankDialog(false)}>
                                {t('cancel')}
                              </Button>
                              <Button onClick={handleAddBank} disabled={addingBank}>
                                {addingBank ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                {t('addAccount')}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {wallet?.bank_accounts?.length > 0 ? (
                        <div className="space-y-4">
                          {wallet.bank_accounts.map((account) => (
                            <div key={account.id} className="flex items-center justify-between p-4 rounded-lg border border-border">
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                                  <Building2 className="w-6 h-6 text-foreground" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-foreground">{account.bank_name}</p>
                                    {account.is_default && (
                                      <Badge className="bg-emerald-500/20 text-emerald-400">{t('default')}</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {account.account_type} •••• {account.account_last4}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {!account.is_default && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => handleSetDefault(account.id)}
                                  >
                                    <Star className="w-4 h-4 mr-1" /> {t('setDefault')}
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="text-red-500 hover:text-red-600"
                                  onClick={() => handleRemoveBank(account.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <CreditCard className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                          <p className="text-muted-foreground">{t('noBankAccountsAdded')}</p>
                          <p className="text-sm text-muted-foreground mb-4">
                            {t('addBankAccountToReceive')}
                          </p>
                          <Button onClick={() => setShowAddBankDialog(true)}>
                            <Plus className="w-4 h-4 mr-2" /> {t('addBankAccountTitle')}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WalletPage;
